import { gsap } from 'gsap';
import type { ThreeStage } from '../../../three/ThreeStage';
import { BaseScene } from '../../../core/BaseScene';
import { glyphGroup, type GlyphId } from '../../../components/glyphs';
import { seeded } from '../../../components/random';
import { draw, pulse } from '../../../components/svg';
import { words, revealWords, settleWords } from '../../../components/text';
import { productReveal, playProductReveal, settleProductReveal } from '../../../components/reveal';

/**
 * Topics headlines are filed under, set in sentence case as format labels (ClimateViewPoint
 * guideline pp. 12, 31). Headlines themselves are drawn as unreadable rules: the point is volume,
 * not content (CLIMATEVIEWPOINT.md §13).
 */
const TOPICS = ['Policy', 'Energy', 'Research', 'Finance', 'Climate', 'Circularity', 'Water', 'Biodiversity', 'Reporting', 'Supply chain'];
const LANES = 9;
const CALM_LANES = 5;

/** The behind-the-scenes flow (§6): sources → ingest → organise → filter → the reader. */
const SOURCES_Y = [200, 270, 340, 410, 480, 550, 620];
const STAGES = [
  { label: 'Ingest', detail: 'fetch · de-duplicate', x: 520 },
  { label: 'Organise', detail: 'topic · source', x: 780 },
  { label: 'Filter', detail: 'relevance', x: 1040 },
];
const PIPE_Y = 410;
/** Fewer items leave each stage than enter it: noise falls away. */
const FLOW_COUNTS = [7, 5, 3, 2];
const DESTINATIONS: ReadonlyArray<{ label: string; glyph: GlyphId; y: number }> = [
  { label: 'For you', glyph: 'star', y: 290 },
  { label: 'All news', glyph: 'news', y: 410 },
  { label: 'Bookmarks', glyph: 'bookmark', y: 530 },
];
const DEST_X = 1340;
/** What feeds personalisation back (§5, §8). */
const SIGNALS = ['Preferences', 'Bookmarks', 'Engagement'];
const LOOP_Y = 720;

/**
 * 0 Headlines stream past — THE STORY CHANGES EVERY DAY.
 * 1 More arrive, faster — YOUR ATTENTION DOESN'T SCALE WITH IT.
 * 2 Everything slows — Sustain Insight
 * 3 Sources → ingest → organise → filter → For You, All News, Bookmarks
 * 4 Preferences, bookmarks and engagement feed back — LESS NOISE. MORE RELEVANCE.
 */
export class ClimateViewPointScene extends BaseScene {
  public readonly stepCount = 5;
  private lanes: gsap.core.Tween[] = [];
  private flowTweens: gsap.core.Tween[] = [];
  private flowKey = 0;

  protected template(): string {
    const random = seeded(517);
    const lanes = Array.from({ length: LANES }, (_, lane) => {
      const cards = Array.from({ length: 8 }, () => {
        const topic = TOPICS[Math.floor(random() * TOPICS.length)];
        const w1 = 60 + Math.round(random() * 30);
        const w2 = 35 + Math.round(random() * 40);
        return `<span class="cvp-card"><b>${topic}</b><i style="width:${w1}%"></i><i style="width:${w2}%"></i></span>`;
      }).join('');
      return `<div class="cvp-lane${lane >= CALM_LANES ? ' cvp-lane--extra' : ''}" style="--y:${(4 + lane * 8.2).toFixed(1)};--depth:${random().toFixed(2)}"><div class="cvp-track">${cards}${cards}</div></div>`;
    }).join('');

    const sources = SOURCES_Y.map((y, i) => `
      <path class="cvp-in draw" pathLength="1" d="M250 ${y} C380 ${y} 400 ${PIPE_Y} ${STAGES[0].x - 40} ${PIPE_Y}"/>
      <circle class="cvp-source" cx="240" cy="${y}" r="8"/>
      ${i === 0 ? '<text class="cvp-label cvp-label--muted" x="240" y="168" text-anchor="middle">Sources</text>' : ''}`).join('');
    const stages = STAGES.map((s, i) => `
      ${i > 0 ? `<path class="cvp-pipe draw" pathLength="1" d="M${STAGES[i - 1].x + 40} ${PIPE_Y} H${s.x - 40}"/>` : ''}
      <g class="cvp-stage" data-index="${i}">
        <rect x="${s.x - 40}" y="${PIPE_Y - 70}" width="80" height="140" rx="16"/>
        <text class="cvp-label" x="${s.x}" y="${PIPE_Y + 108}" text-anchor="middle">${s.label}</text>
        <text class="cvp-label cvp-label--muted" x="${s.x}" y="${PIPE_Y + 132}" text-anchor="middle">${s.detail}</text>
      </g>`).join('');
    const dests = DESTINATIONS.map((d) => `
      <path class="cvp-out draw" pathLength="1" d="M${STAGES[2].x + 40} ${PIPE_Y} C${STAGES[2].x + 140} ${PIPE_Y} ${DEST_X - 170} ${d.y} ${DEST_X - 110} ${d.y}"/>
      <g class="cvp-dest">
        <rect x="${DEST_X - 106}" y="${d.y - 34}" width="220" height="68" rx="14"/>
        <g transform="translate(${DEST_X - 72} ${d.y})">${glyphGroup(d.glyph, 26)}</g>
        <text class="cvp-dest__label" x="${DEST_X - 44}" y="${d.y + 7}">${d.label}</text>
      </g>`).join('');
    const pulses = FLOW_COUNTS.map((n, stage) => Array.from({ length: n }, () => `<circle class="cvp-pulse" data-stage="${stage}" r="4"/>`).join('')).join('');
    const loopPath = `M${DEST_X} ${DESTINATIONS[2].y + 40} C${DEST_X} ${LOOP_Y} ${DEST_X - 120} ${LOOP_Y} ${DEST_X - 300} ${LOOP_Y} H${STAGES[2].x + 120} C${STAGES[2].x} ${LOOP_Y} ${STAGES[2].x} ${LOOP_Y - 60} ${STAGES[2].x} ${PIPE_Y + 150}`;
    const signals = SIGNALS.map((s, i) => `<g class="cvp-signal"><rect x="${1100 + (i - 1) * 150 - 64}" y="${LOOP_Y - 17}" width="128" height="34" rx="17"/><text x="${1100 + (i - 1) * 150}" y="${LOOP_Y + 5}" text-anchor="middle">${s}</text></g>`).join('');

    return `
      <div class="cvp-lanes" aria-hidden="true">${lanes}</div>
      <div class="scene-layout climateviewpoint">
        <svg class="diagram cvp-diagram" viewBox="0 0 1600 900" role="img" aria-label="Sustainability news from many sources ingested, organised and filtered into For You, All News and Bookmarks, with preferences, bookmarks and engagement feeding back">
          <g class="cvp-flow" data-from="3">
            ${sources}
            ${stages}
            ${dests}
            <g class="cvp-pulses">${pulses}</g>
          </g>
          <g class="cvp-loop" data-from="4">
            <path class="cvp-loop__path draw" pathLength="1" d="${loopPath}"/>
            ${signals}
            <circle class="cvp-loop__pulse" r="5"/>
          </g>
        </svg>
        <div class="cvp-statements">
          <h2 class="statement" data-from="0" data-until="0">${words('The story changes every day.')}</h2>
          <h2 class="statement statement--accent" data-from="1" data-until="1">${words("Your attention doesn't scale with it.")}</h2>
          <h2 class="statement statement--accent" data-from="4">${words('Less noise. More relevance.')}</h2>
        </div>
        ${productReveal('climateviewpoint', 2, '', 2)}
      </div>`;
  }

  protected onEnter(immediate: boolean): void {
    this.startLanes();
    this.applyLanes(this.step, true);
    if (immediate || this.step !== 0) return;
    const tl = this.timeline();
    tl.fromTo(this.$$('.cvp-lane:not(.cvp-lane--extra)'), { opacity: 0 }, { opacity: 1, duration: 1.2, stagger: 0.1 }, 0);
    tl.add(revealWords(this.$('.cvp-statements .statement')), 0.8);
  }

  protected onExit(): void {
    this.lanes = [];
    this.flowTweens = [];
    this.flowKey = 0;
  }

  private startLanes(): void {
    if (this.lanes.length) return;
    this.lanes = this.$$('.cvp-track').map((track, i) =>
      this.loop(gsap.fromTo(track, { xPercent: 0 }, { xPercent: -50, duration: 38 + (i % 4) * 7, ease: 'none', repeat: -1 })));
  }

  /** Lane speed and visibility per step: calm, then more and faster, then slowing to a stop. */
  private applyLanes(step: number, immediate: boolean): void {
    const speed = step === 0 ? 1 : step === 1 ? 2.6 : 0.15;
    this.lanes.forEach((lane) => gsap.to(lane, { timeScale: speed, duration: immediate ? 0 : 1.4, ease: 'power2.inOut', overwrite: true }));
    const d = immediate ? 0 : 0.9;
    gsap.to(this.$$('.cvp-lane:not(.cvp-lane--extra)'), { opacity: step <= 1 ? 1 : step === 2 ? 0.25 : 0, duration: d });
    gsap.to(this.$$('.cvp-lane--extra'), { opacity: step === 1 ? 1 : step === 2 ? 0.2 : 0, duration: d, stagger: immediate ? 0 : 0.12 });
  }

  protected onStep(step: number, stage: ThreeStage, immediate: boolean, previousStep: number): void {
    stage.setAccent(this.meta.accent);
    stage.setMode(this.meta.id, step, immediate);
    const forward = !immediate && step > previousStep;
    const tl = this.timeline();

    this.$$('.cvp-statements .statement').forEach((s) => settleWords(s));
    if (forward && step === 1) tl.add(revealWords(this.$$('.cvp-statements .statement')[1]), 0.2);
    if (forward && step === 4) tl.add(revealWords(this.$$('.cvp-statements .statement')[2]), 1);
    if (this.lanes.length) this.applyLanes(step, immediate);

    if (forward && step === 2) playProductReveal(this.root, tl, 0.8);
    else settleProductReveal(this.root);

    // 3 — the flow draws left to right; each stage lets less through.
    const flowPaths = this.$$('.cvp-in, .cvp-pipe, .cvp-out');
    const boxes = this.$$('.cvp-stage, .cvp-dest, .cvp-source');
    if (forward && step === 3) {
      draw(flowPaths, false, { immediate: true });
      tl.fromTo(this.$$('.cvp-source'), { opacity: 0 }, { opacity: 1, duration: 0.4, stagger: 0.05 }, 0.2);
      tl.add(draw(this.$$('.cvp-in'), true, { duration: 0.8, stagger: 0.05 }), 0.4);
      this.$$('.cvp-stage').forEach((el, i) => {
        tl.fromTo(el, { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.5, ease: 'expo.out' }, 1 + i * 0.4);
        const pipe = this.$$('.cvp-pipe')[i - 1];
        if (pipe) tl.add(draw(pipe, true, { duration: 0.4 }), 0.8 + i * 0.4);
      });
      tl.add(draw(this.$$('.cvp-out'), true, { duration: 0.7, stagger: 0.12 }), 2.2);
      tl.fromTo(this.$$('.cvp-dest'), { opacity: 0, x: -12 }, { opacity: 1, x: 0, duration: 0.6, stagger: 0.15, ease: 'expo.out' }, 2.5);
    } else {
      draw(flowPaths, step >= 3, { immediate: true });
      gsap.set(boxes, { opacity: 1, x: 0, y: 0 });
    }

    // 4 — what the reader saves and engages with feeds back into what they see.
    const loopPath = this.$('.cvp-loop__path');
    const signals = this.$$('.cvp-signal');
    if (forward && step === 4) {
      draw(loopPath, false, { immediate: true });
      tl.add(draw(loopPath, true, { duration: 1.3, ease: 'power2.inOut' }), 0.2);
      tl.fromTo(signals, { opacity: 0, y: 8 }, { opacity: 1, y: 0, duration: 0.5, stagger: 0.15 }, 0.6);
    } else {
      draw(loopPath, step >= 4, { immediate: true });
      gsap.set(signals, { opacity: 1, y: 0 });
    }
    this.$('.cvp-diagram').dataset.personal = String(step >= 4);

    this.syncFlow(step >= 3, forward && step === 3 ? 2.6 : 0, step >= 4);
  }

  private syncFlow(on: boolean, delay: number, loop: boolean): void {
    const key = on ? (loop ? 2 : 1) : 0;
    if (key === this.flowKey) return;
    this.flowKey = key;
    // Lanes are loops too; keep them and restart only the flow markers.
    this.flowTweens.forEach((t) => t.kill());
    this.flowTweens = [];
    this.$$<SVGCircleElement>('.cvp-pulse, .cvp-loop__pulse').forEach((m) => { m.style.opacity = '0'; });
    if (!key) return;
    const groups = [this.$$<SVGPathElement>('.cvp-in'), this.$$<SVGPathElement>('.cvp-pipe').slice(0, 1), this.$$<SVGPathElement>('.cvp-pipe').slice(1, 2), this.$$<SVGPathElement>('.cvp-out')];
    this.$$<SVGCircleElement>('.cvp-pulse').forEach((m) => {
      const stage = Number(m.dataset.stage);
      const paths = groups[stage];
      const index = this.$$<SVGCircleElement>(`.cvp-pulse[data-stage="${stage}"]`).indexOf(m);
      const path = paths[index % paths.length];
      this.flowTweens.push(this.loop(pulse(m, path, { duration: 1 + stage * 0.1, delay: delay + stage * 0.35 + index * 0.3, repeatDelay: 1.2 + index * 0.2 })));
    });
    if (loop) this.flowTweens.push(this.loop(pulse(this.$<SVGCircleElement>('.cvp-loop__pulse'), this.$<SVGPathElement>('.cvp-loop__path'), { duration: 2.4, delay: 1.2, repeatDelay: 0.6, ease: 'none' })));
  }

}
